<template>
  <div
    :class="`${slideData.customClass} ${slideData.theme ? `stage-slide--${slideData.theme}` : ''}`"
    :style="cssBackground"
    class="stage-slide"
  >
    <CdnImage
      v-if="slideData.background?.image?.src"
      :alt="slideData.background.image.alt || ''"
      :src="slideData.background.image.src"
      :src-sm="slideData.background.image.srcSm"
      :src-md="slideData.background.image.srcMd"
      :aspect-ratio="slideData.background.image.aspectRatio"
      :aspect-ratio-sm="slideData.background.image.aspectRatioSm"
      :aspect-ratio-md="slideData.background.image.aspectRatioMd"
      :sizes="slideData.background.image.sizes || ''"
      :do-not-convert="!slideData.background.image.sizes"
      :lazy="lazyLoading"
      :preload="!lazyLoading"
      class="stage-slide__background"
    />
    <div class="stage-slide__container">
      <div class="stage-slide__content">
        <div
          :class="cssTheme"
          class="stage-slide__information"
        >
          <div class="stage-slide__information-container">
            <div class="stage-slide__headline-wrapper">
              <CdnImage
                v-if="brand"
                class="stage-slide__headline-brand"
                :alt="brand.alt || brand.name"
                :src="brand.src || brand.logo"
                :style="brand.style"
                :sizes="brand.sizes"
                render-without-figure
                :lazy="lazyLoading"
                :preload="!lazyLoading"
              />
              <Paragraph
                :text="headline"
                :theme="slideData.theme"
                class="stage-slide__headline"
              />
            </div>
            <p
              v-if="subline"
              class="text stage-slide__subline"
            >
              <span v-html="subline"></span>
              <LinkButton
                v-if="slideData.modal?.opener?.includes('footnote')"
                tag="button"
                class="btn btn--footnote"
                type="button"
                aria-label="Fußnote"
                @click="openModalHandler"
              >
                *
              </LinkButton>
            </p>
            <div
              v-if="!ctaButton?.hideButton"
              class="stage-slide__button-container stage-slide__button-container--desktop"
            >
              <StageLink
                v-if="ctaButton.url"
                class="stage-slide__stage-link"
                :class="btnTheme"
                :is-external="ctaButton.isExternal"
                :url="ctaButton.url"
                :webtrack="dataTealiumRel"
              >
                {{ ctaButton.buttonText }}
              </StageLink>
              <StageLink
                v-if="slideData.secondaryCtaButton"
                class="stage-slide__stage-link"
                :class="btnTheme"
                :is-external="slideData.secondaryCtaButton.isExternal || false"
                :url="slideData.secondaryCtaButton.url"
                :webtrack="dataTealiumRel"
              >
                {{ slideData.secondaryCtaButton.buttonText }}
              </StageLink>
              <LinkButton
                v-if="slideData.modal?.opener?.includes('button')"
                :class="btnTheme"
                tag="button"
                class="stage-slide__stage-link"
                type="button"
                @click="openModalHandler"
              >
                {{ slideData.modal.openButtonLabel || 'Details' }}
              </LinkButton>
            </div>
          </div>
        </div>
        <div
          v-if="visual"
          :class="hasVisualAlignment"
          class="stage-slide__visual-container"
        >
          <CdnImage
            v-if="visual.src"
            :alt="visual.alt || ''"
            :src="visual.src"
            :src-sm="visual.srcSm"
            :src-md="visual.srcMd"
            :aspect-ratio="visual.aspectRatio"
            :aspect-ratio-sm="visual.aspectRatioSm"
            :aspect-ratio-md="visual.aspectRatioMd"
            :image-style="visual.style"
            :sizes="visual.sizes"
            class="stage-slide__visual"
            :lazy="lazyLoading"
            :preload="!lazyLoading"
          />
          <PromotionBadge
            :badge="slideData.badge"
            :offer="offer"
            :modal-texts="slideData.badgeModal"
          >
            <LinkButton
              v-if="slideData.modal?.opener?.includes('badge')"
              class="btn btn--footnote"
              tag="button"
              type="button"
              aria-label="Fußnote"
              @click="openModalHandler"
            >
              *
            </LinkButton>
          </PromotionBadge>
          <PricesContainer
            v-if="slideData.price && offer?.prices"
            class="stage-slide__prices"
            variant="medium"
            is-price-bold
            :prices-data="offer.prices"
            :pre-text-is-price="slideData.price.preTextIsPrice"
            :pre-text-was-price="slideData.price.preTextWasPrice"
            :style="slideData.price.styles"
            :theme="slideData.theme"
          />
          <Prices
            v-else-if="slideData.price?.default"
            class="stage-slide__prices"
            :is-price="slideData.price.default"
            :was-price="slideData.price.was"
            is-price-bold
            :pre-text-is-price="slideData.price.preTextIsPrice"
            :pre-text-was-price="slideData.price.preTextWasPrice"
            :theme="slideData.theme"
            :style="slideData.price.styles"
          />
        </div>
      </div>
      <div
        v-if="!ctaButton.hideButton"
        class="stage-slide__button-container stage-slide__button-container--mobile"
      >
        <StageLink
          v-if="ctaButton.url"
          class="stage-slide__stage-link"
          :class="btnTheme"
          :is-external="ctaButton.isExternal"
          :url="ctaButton.url"
          :webtrack="dataTealiumRel"
        >
          {{ ctaButton.buttonText }}
        </StageLink>
        <StageLink
          v-if="slideData.secondaryCtaButton"
          class="stage-slide__stage-link"
          :class="btnTheme"
          :is-external="slideData.secondaryCtaButton.isExternal"
          :url="slideData.secondaryCtaButton.url"
          :webtrack="dataTealiumRel"
        >
          {{ slideData.secondaryCtaButton.buttonText }}
        </StageLink>
        <LinkButton
          v-if="slideData.modal?.opener?.includes('button')"
          class="stage-slide__stage-link"
          :class="btnTheme"
          type="button"
          tag="button"
          @click="openModalHandler"
        >
          {{ slideData.modal.openButtonLabel || 'Details' }}
        </LinkButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, withDefaults } from 'vue';
import { getPrice } from '@i22/rocket';
import CdnImage from '~/components/cdn-image.vue';
import StageLink from '~/components/stage-link.vue';
import { Paragraph, LinkButton, Prices, type ModalButton } from '@i22/td-longtail-vue-components';
import { openModal } from '~/composables/modal';
import { getMainImageFromOffer } from '~/lib/goliath-media';
import type { PromotionOffer } from '~/lib/offers-by-group';
import type { ListOffer } from '~/lib/goliath/offers';
import type { CTA, SlideVisual, StageSliderData } from '~/lib/promotions-type';
import { addLogo, getBrand } from '~/lib/brand';
import PricesContainer from '~/components/product-detail/prices-container.vue';
import PromotionBadge from '~/components/promotion-badge.vue';

export type StageSlideComponentProps = {
  slideData: PromotionOffer<StageSliderData>;
  lazyLoading?: boolean;
};

const props = withDefaults(defineProps<StageSlideComponentProps>(), {});

const cssBackground = computed(() => {
  const styles = props.slideData.style || {};

  if (props.slideData.background?.color) {
    styles.background = props.slideData.background.color;
  }
  return styles;
});

const btnTheme = computed(() => {
  const baseStyle = 'stage-slide__button';
  if (props.slideData.ctaButton?.primary) {
    return `${baseStyle} btn--primary`;
  }
  if (props.slideData.ctaButton?.secondary) {
    return `${baseStyle} btn--secondary`;
  }
  if (props.slideData.ctaButton?.outline) {
    return `${baseStyle} btn--outline`;
  }
  if (props.slideData.ctaButton?.theme) {
    return `${baseStyle} stage-slide__button--${props.slideData.ctaButton.theme} ${
      props.slideData.ctaButton.theme === 'light' ? 'btn--outline-light' : 'btn--outline'
    }`;
  }
  if (props.slideData.theme === 'light')
    return `${baseStyle} stage-slide__button--light btn--outline-light`;
  return `${baseStyle} stage-slide__button--dark btn--outline`;
});

const cssTheme = computed(() => {
  if (props.slideData.theme === 'light') return 'stage-slide__information--light';
  return null;
});

const offer = computed<ListOffer | undefined>(() => props.slideData.offer);

const defaultPrice = computed(() => {
  if (!offer.value?.prices) return undefined;
  const price = getPrice(offer.value.prices || [], 'DEFAULT', 'ONETIME');
  return price?.amount;
});

const wasPrice = computed(() => {
  if (!offer.value?.prices) return undefined;
  const price = getPrice(offer.value.prices || [], 'WAS', 'ONETIME');
  return price?.amount;
});

const headline = computed(() =>
  props.slideData.headline ? props.slideData.headline.text : offer.value?.product?.name
);

const brand = computed(() => {
  if (props.slideData.headline?.brand?.src) {
    return props.slideData.headline?.brand;
  }
  if (props.slideData.headline?.brand?.slug) {
    const brandData = addLogo({
      slug: props.slideData.headline.brand.slug,
      name: props.slideData.headline.brand.alt,
    });
    if (brandData) {
      return {
        ...brandData,
        ...props.slideData.headline?.brand,
        style: `aspect-ratio: ${brandData.aspectRatio};${props.slideData.headline?.brand?.style}`,
      };
    }
  }
  if (offer.value) {
    const brandData = getBrand(offer.value);
    if (brandData) {
      return {
        ...brandData,
        ...props.slideData.headline?.brand,
        style: `aspect-ratio: ${brandData.aspectRatio};${props.slideData.headline?.brand?.style}`,
      };
    }
  }

  return undefined;
});

const headingTag = computed(() => {
  if (!props.slideData.headline?.tag) return 'h2';
  return props.slideData.headline.tag;
});
const subline = computed(() => props.slideData.subline);

const slug = computed(() => offer.value?.product?.slug);

const ctaButton = computed<CTA & StageSliderData['ctaButton']>(() => ({
  buttonText: 'Zum Angebot',
  url: slug.value ? `/geraete/${slug.value}` : undefined,
  ...props.slideData.ctaButton,
}));

// srcMedium and srcLarge is possible as well
const visual = computed<SlideVisual & StageSliderData['visual']>(() => {
  const defaultVisual: SlideVisual = {
    src: undefined,
    verticalAlignment: 'center',
  };

  if (offer.value && !props.slideData.visual?.src) {
    const goliathImage = getMainImageFromOffer(offer.value, 0);
    if (goliathImage) {
      defaultVisual.src = goliathImage.small;
      defaultVisual.srcSm = goliathImage.medium;
      defaultVisual.srcMd = goliathImage.large;
      defaultVisual.aspectRatio = '1';
    }
  }

  return {
    ...defaultVisual,
    ...props.slideData.visual,
  };
});

const hasVisualAlignment = computed(() => {
  if (!visual.value.verticalAlignment) return 'stage-slide__visual-container--vertical-center';
  return `stage-slide__visual-container--vertical-${visual.value.verticalAlignment}`;
});

const dataTealiumRel = computed(() => {
  if (!ctaButton.value.buttonText) return '';
  return `content.button.${ctaButton.value.buttonText}`;
});

const openModalHandler = () => {
  const buttons: (ModalButton | Record<string, any>)[] = [
    {
      name: 'Schließen',
      tag: 'button',
      outline: true,
    },
  ];
  const modalBtn = props.slideData.modal?.button;
  if (modalBtn) {
    buttons.push({
      name: modalBtn.label,
      tag: modalBtn.link?.startsWith('http') ? 'a' : 'nuxt-link',
      to: modalBtn.link,
      outline: true,
    });
  }
  openModal({
    component: {
      is: defineAsyncComponent(() => import('~/components/stage-slide-dialog.vue')),
      props: {
        slideData: props.slideData,
      },
    },
    className: `stage-slide__modal stage-slide__modal--${props.slideData.modal?.id}`,
    buttons,
  });
};

defineExpose({
  cssBackground,
  btnTheme,
  cssTheme,
  hasVisualAlignment,
  defaultPrice,
  wasPrice,
  headline,
  headingTag,
  subline,
  ctaButton,
  visual,
  slug,
});
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.stage-slide {
  position: relative;
  display: grid;
  text-align: center;
  width: 100%;

  @include mq($mq-sm) {
    justify-content: flex-start;
    text-align: left;
    min-height: initial;
  }
  @include mq($mq-md) {
    justify-content: center;
  }

  &__badge {
    position: absolute;
  }

  &__container,
  &__background {
    grid-row: 1;
    grid-column: 1;
  }

  &__background {
    position: relative;
    display: block;
    width: 100vw;
    height: 100%;
    pointer-events: none;

    :deep(img) {
      object-fit: cover;
      display: block;
      width: 100%;
      height: 100%;

      object-position: var(--background-position-xs, 50% 50%);

      @include mq($mq-sm) {
        object-position: var(--background-position-sm, 50% 50%);
      }
      @include mq($mq-md) {
        object-position: var(--background-position-md, 50% 50%);
      }
    }
  }

  &__container {
    width: 100%;
    height: 100%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    max-width: 320px - $grid-base * 2;
    padding-top: $grid-base * 2;
    padding-bottom: $grid-base;

    @include mq(375px) {
      max-width: 400px;
    }
    @include mq($mq-sm) {
      flex-direction: row;
      justify-content: initial;
      max-width: 80%;
    }
    @include mq($mq-md) {
      width: 100%;
      max-width: 90%;
      padding: 0;
    }
    @include mq(1200px) {
      max-width: 1000px;
    }
  }

  &__content {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: var(--visual-justify-xs, flex-start);

    @include mq($mq-sm) {
      flex-direction: row;
      justify-content: var(--visual-justify-sm, flex-start);
    }
  }

  &__button-container {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    text-align: center;
    justify-content: center;

    @include mq($mq-sm) {
      padding-top: 2 * $grid-base;
      text-align: left;
      justify-content: flex-start;
    }

    &--mobile {
      @include mq($mq-sm) {
        display: none;
      }
    }

    &--desktop {
      @include mq(false, $mq-sm - 1) {
        display: none;
      }
    }
  }

  &__button {
    position: relative;
  }

  &__headline {
    font-weight: 800;
    margin-bottom: $grid-base;
    text-align: center;
    @include font-size(27px, 30px);

    @include mq($mq-sm) {
      text-align: left;
      @include font-size(36px, 38px);
    }
    @include mq($mq-md) {
      @include font-size(48px, 54px);
    }

    &-brand {
      height: 40px;
      display: inline-block;
      filter: brightness(1.25) contrast(0.9); // convert color #262626 -> #383838

      @include mq($mq-sm, $mq-md - 1) {
        height: 30px;
      }
    }
  }

  &__information {
    display: flex;
    align-items: center;
    z-index: 5;
    position: relative;
    color: color(grey, 12000);
    padding: 0 $grid-base;

    &--light {
      color: color(white);
      .stage-slide__headline-brand {
        filter: contrast(0) brightness(2);
      }
    }

    @include mq($mq-sm) {
      width: calc(100% - var(--visual-width, 60%));
    }

    @include mq($mq-md) {
      padding-right: $grid-base * 2;
    }

    &-container {
      width: 100%;
    }
  }

  &__visual-container {
    position: relative;

    &--vertical-center {
      text-align: center;
    }

    @include mq($mq-sm) {
      display: flex;
      width: var(--visual-width, 40%);
      flex-direction: row-reverse;

      &--vertical-center {
        align-items: center;
      }
      &--vertical-top {
        align-items: flex-start;
      }
      &--vertical-bottom {
        align-items: flex-end;
      }
    }
  }

  &__visual {
    flex: 0 0 100%;

    :deep(img) {
      display: block;
      width: 100%;
      object-fit: contain;
      object-position: center;

      @include mq(false, $mq-sm - 1) {
        height: 200px;
      }

      @include mq($mq-sm) {
        height: 100%;
      }
    }
  }

  &__subline {
    @include font-size(18px, 22px);
    margin-bottom: 12px;

    @include mq($mq-sm) {
      @include font-size(21px, 24px);
      margin-bottom: 32px;
    }
    @include mq($mq-md) {
      @include font-size(24px, 28px);
      margin-bottom: 42px;
    }
  }

  &__modal {
    &-image {
      width: 270px;
      margin-bottom: 2 * $grid-base;
    }

    &-subheading {
      font-weight: 800;
      @include font-size(24px, 29px);
      text-align: center;
    }

    &-heading {
      color: var(--color-primary);
      margin-bottom: $grid-base * 2;
    }
  }

  &__prices {
    display: inline-block;
    margin-top: $grid-base * 2;

    @include mq($mq-sm) {
      margin-top: 50%;

      :deep(.prices__main) {
        flex-direction: column;
        grid-gap: 0;
      }
      :deep(.price) {
        flex-direction: row;
        flex-wrap: nowrap;
      }
    }
  }

  &--light {
    background-color: color(black);
  }
}

.stage--apple-iphone-17-acc {
  .stage-slide__subline {
    @include mq($mq-sm, $mq-md - 1) {
      margin-bottom: 0;
    }
  }

  .stage-slide__background {
    :deep(img) {
      @include mq($mq-xl) {
        object-fit: contain;
      }
    }
  }
}

.stage--heating {
  --visual-width: 40%;

  @include mq($mq-sm) {
    --visual-width: 47%;
  }

  @include mq($mq-md) {
    --visual-width: 50%;
  }

  .stage-slide__container {
    @include mq($mq-sm, $mq-md - 1) {
      max-width: 90%;
    }
  }

  .stage-slide__visual {
    :deep(img) {
      @include mq(false, $mq-sm - 1) {
        margin-bottom: $grid-base * 2;
      }
    }
  }
}
</style>
